"use client";

import {
  CircleAlert,
  MapPin,
  MessageCircle,
  Search,
  Store,
  Truck,
  WifiOff,
} from "lucide-react";
import { useEffect, useId, useState, type ReactNode } from "react";
import { Button } from "@/components/UI/Button";
import { LinkWhatsApp } from "@/components/UI/LinkWhatsApp";
import { useShippingCalculator } from "@/hooks/useShippingCalculator";
import { STORE_INFO } from "@/lib/constants/storeInfo";
import { isZeroMoney } from "@/lib/formatting/money";
import { buildSellerWhatsappMessage, type SellerWhatsappAddress } from "@/lib/commerce/sellerWhatsappMessage";
import type {
  ProductShippingInput,
  SelectedShippingRate,
} from "@/types/shipping";
import { ShippingOptions } from "./ShippingOptions";
import { ZipCodeInput } from "./ZipCodeInput";

interface ShippingCalculatorProps {
  contextKey: string;
  mode: "cart" | "product";
  onSelectionChange?: (selection?: SelectedShippingRate) => void;
  product?: ProductShippingInput;
  /** Nome e link do produto: vão na mensagem ao vendedor quando não há transportadora. */
  productInfo?: { name: string; url?: string };
  /** Layout enxuto (página do carrinho). */
  compact?: boolean;
}

interface FeedbackCardProps {
  children?: ReactNode;
  icon: ReactNode;
  text: string;
  title: string;
  tone?: "informative" | "danger";
}

function FeedbackCard({
  children,
  icon,
  text,
  title,
  tone = "informative",
}: FeedbackCardProps) {
  const danger = tone === "danger";

  return (
    <div
      className={`rounded-xl border p-4 ${
        danger
          ? "border-red-200 bg-red-50 text-red-900"
          : "border-blue-100 bg-blue-50/70 text-foreground"
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={danger ? "text-red-700" : "text-primary"}
          aria-hidden="true"
        >
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-current">{title}</h3>
          <p className="mt-1 text-sm leading-6">{text}</p>
          {children ? <div className="mt-4">{children}</div> : null}
        </div>
      </div>
    </div>
  );
}

// Sem transportadora: busca rua/cidade do CEP para a mensagem ao vendedor.
// Se a busca falhar, a mensagem vai só com o CEP.
function usePostcodeAddress(postcode: string, enabled: boolean): SellerWhatsappAddress | null {
  const digits = postcode.replace(/\D/g, "");
  const [found, setFound] = useState<{ digits: string; address: SellerWhatsappAddress | null } | null>(null);
  useEffect(() => {
    if (!enabled || digits.length !== 8) return;
    const controller = new AbortController();
    fetch(`/api/cep/${digits}`, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { address?: SellerWhatsappAddress | null } | null) => setFound({ digits, address: body?.address ?? null }))
      .catch(() => {
        // abortado ou sem rede: segue só com o CEP
      });
    return () => controller.abort();
  }, [digits, enabled]);
  return found?.digits === digits ? found.address : null;
}

export function ShippingCalculator(props: ShippingCalculatorProps) {
  const inputId = useId();
  const calculator = useShippingCalculator(props);
  const destination = calculator.quote.destination;
  const destinationParts = [
    destination?.address1,
    destination?.address2,
    destination?.city,
    destination?.state,
  ].filter(Boolean);
  const rates = calculator.quote.packages.flatMap(
    (shippingPackage) => shippingPackage.rates,
  );
  // Retirada na loja é sempre grátis (o cliente busca), não é bonificação:
  // só entrega com preço zero conta como "Frete grátis para sua região".
  const hasFreeShipping = rates.some(
    (rate) => rate.methodId !== "local_pickup" && isZeroMoney(rate.price),
  );
  const hasStorePickup = rates.some((rate) => rate.methodId === "local_pickup");
  const compact = props.compact === true;
  const noCarrier = calculator.status === "no_carrier";
  const noCarrierAddress = usePostcodeAddress(calculator.postcode, noCarrier);
  const sellerMessage = noCarrier
    ? buildSellerWhatsappMessage({
        product: props.productInfo
          ? {
              name: props.productInfo.name,
              url: props.productInfo.url,
              quantity: props.product?.quantity,
              variation: props.product?.variation,
            }
          : undefined,
        postcode: calculator.postcode,
        address: noCarrierAddress,
      })
    : undefined;
  // CEP completo (8 dígitos): calcula sozinho, sem precisar do botão.
  const changePostcode = (value: string) => {
    calculator.setPostcode(value);
    if (value.replace(/\D/g, "").length === 8) void calculator.calculate(value);
  };

  if (compact) {
    return (
      <section className="rounded-xl border border-slate-200 p-3">
        <h2 className="flex items-center gap-1.5 text-sm font-bold text-heading">
          <MapPin className="h-4 w-4 text-secondary" aria-hidden="true" />
          Simule o frete
        </h2>
        {calculator.status === "success" && calculator.postcode ? (
          <p className="mt-2 flex items-start justify-between gap-2 text-xs text-foreground">
            <span className="min-w-0">
              <strong>CEP {calculator.postcode}</strong>
              {destinationParts.length ? <span className="block truncate text-muted">{destinationParts.join(", ")}</span> : null}
            </span>
            <button type="button" onClick={calculator.reset} className="shrink-0 font-semibold text-secondary underline underline-offset-2">
              Alterar
            </button>
          </p>
        ) : (
          <div className="mt-2">
            <ZipCodeInput
              id={`shipping-postcode-${inputId}`}
              value={calculator.postcode}
              isLoading={calculator.isLoading}
              onChange={changePostcode}
              onSubmit={() => void calculator.calculate()}
            />
          </div>
        )}
        {calculator.isLoading ? (
          <p className="mt-2 text-xs font-medium text-heading" role="status">Calculando opções de entrega...</p>
        ) : calculator.quote.packages.length ? (
          <div className="mt-2">
            <ShippingOptions
              compact
              packages={calculator.quote.packages}
              selection={calculator.selection}
              disabled={calculator.isLoading}
              selectable={props.mode === "cart"}
              onSelect={(packageId, rateId) => void calculator.chooseRate(packageId, rateId)}
            />
          </div>
        ) : null}
        <div role="status" aria-live="polite" aria-atomic="true" className="text-xs">
          {calculator.status === "empty" ? (
            <p className="mt-2 text-red-800">
              Ainda não entregamos nesse CEP.{" "}
              <LinkWhatsApp posicao="calculadora_frete" fallbackHref={STORE_INFO.whatsapp.href} className="font-semibold underline">
                Fale com a gente
              </LinkWhatsApp>
            </p>
          ) : null}
          {calculator.status === "no_carrier" ? (
            <p className="mt-2 text-red-800">
              Sem transportadora para este CEP.{" "}
              <LinkWhatsApp posicao="calculadora_frete_sem_transportadora" fallbackHref={STORE_INFO.whatsapp.href} mensagem={sellerMessage} className="font-semibold underline">
                Fale com um vendedor no WhatsApp
              </LinkWhatsApp>
            </p>
          ) : null}
          {calculator.status === "invalid_cep" ? <p className="mt-2 text-red-800">CEP inválido. Confira os números.</p> : null}
          {calculator.status === "network_error" || calculator.status === "server_error" ? (
            <p className="mt-2 text-red-800">
              Não foi possível calcular o frete agora.{" "}
              <button type="button" onClick={() => void calculator.calculate()} className="font-semibold underline">Tentar novamente</button>
            </p>
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-2">
        <MapPin className="h-5 w-5 text-secondary" aria-hidden="true" />
        <h2 className="font-bold text-heading">Calcular frete e prazo</h2>
      </div>

      {calculator.status === "idle" ? (
        <p className="mt-3 text-sm text-muted" aria-live="polite">
          Informe seu CEP para consultar prazo e valor do frete.
        </p>
      ) : null}

      {calculator.status === "success" && calculator.postcode ? (
        <div className="mt-4 rounded-xl bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
            Entrega para
          </p>
          <p className="mt-1 text-sm text-foreground">
            {destinationParts.length
              ? destinationParts.join(" — ")
              : `CEP ${calculator.postcode}`}
          </p>
          <button
            type="button"
            onClick={calculator.reset}
            className="mt-2 text-sm font-semibold text-secondary underline underline-offset-2"
          >
            Alterar CEP
          </button>
        </div>
      ) : (
        <div className="mt-4">
          <ZipCodeInput
            id={`shipping-postcode-${inputId}`}
            value={calculator.postcode}
            isLoading={calculator.isLoading}
            onChange={changePostcode}
            onSubmit={() => void calculator.calculate()}
          />
        </div>
      )}

      <div
        className={`grid transition-[grid-template-rows,opacity] duration-300 ${
          calculator.isLoading || calculator.quote.packages.length
            ? "mt-4 grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          {calculator.isLoading ? (
            <div
              className="space-y-3"
              role="status"
              aria-label="Calculando opções de entrega"
            >
              <p className="text-sm font-medium text-heading">
                Calculando opções de entrega...
              </p>
              {[1, 2].map((item) => (
                <div
                  key={item}
                  className="h-20 animate-pulse rounded-xl bg-slate-200"
                />
              ))}
            </div>
          ) : (
            <>
              {hasFreeShipping ? (
                <div className="mb-3 flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-3 text-sm font-semibold text-green-800">
                  <Truck className="h-5 w-5" aria-hidden="true" />
                  {calculator.quote.withCart
                    ? "Frete grátis com os produtos do seu carrinho"
                    : "Frete grátis para sua região"}
                </div>
              ) : null}
              {hasStorePickup ? (
                <div className="mb-3 flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 p-3 text-sm font-semibold text-orange-900">
                  <Store className="h-5 w-5" aria-hidden="true" />
                  Retire em nossa loja
                </div>
              ) : null}
              <ShippingOptions
                packages={calculator.quote.packages}
                selection={calculator.selection}
                disabled={calculator.isLoading}
                selectable={props.mode === "cart"}
                onSelect={(packageId, rateId) =>
                  void calculator.chooseRate(packageId, rateId)
                }
              />
            </>
          )}
        </div>
      </div>

      <div className="mt-3" role="status" aria-live="polite" aria-atomic="true">
        {calculator.status === "success" && calculator.message ? (
          <p className="text-sm text-muted">{calculator.message}</p>
        ) : null}

        {calculator.status === "empty" ? (
          <FeedbackCard
            icon={<MapPin className="h-5 w-5" />}
            title="Ainda não entregamos nesse CEP."
            text="No momento não encontramos opções de entrega para esta região. Verifique se o CEP está correto. Caso tenha dúvidas, fale com nossa equipe para consultar uma alternativa."
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button variant="outline" size="sm" onClick={calculator.reset}>
                Alterar CEP
              </Button>
              <a
                href="https://buscacepinter.correios.com.br/app/endereco/index.php?t"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-9 items-center justify-center gap-2 rounded-xl border border-primary px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-blue-100"
              >
                <Search className="h-4 w-4" aria-hidden="true" />
                Pesquisar CEP
              </a>
              <LinkWhatsApp
                posicao="calculadora_frete"
                fallbackHref={STORE_INFO.whatsapp.href}
                className="inline-flex min-h-9 items-center justify-center gap-2 rounded-xl bg-secondary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-orange-700"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                Falar com um consultor
              </LinkWhatsApp>
            </div>
          </FeedbackCard>
        ) : null}

        {calculator.status === "no_carrier" ? (
          <FeedbackCard
            icon={<Truck className="h-5 w-5" />}
            title="Sem transportadora para este CEP."
            text="Fale com um vendedor: a mensagem já vai com o produto e o seu CEP, e respondemos se dá para entregar."
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <LinkWhatsApp
                posicao="calculadora_frete_sem_transportadora"
                fallbackHref={STORE_INFO.whatsapp.href}
                mensagem={sellerMessage}
                className="inline-flex min-h-9 items-center justify-center gap-2 rounded-xl bg-[#16a34a] px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#15803d]"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                Falar com vendedor no WhatsApp
              </LinkWhatsApp>
              <Button variant="outline" size="sm" onClick={calculator.reset}>
                Alterar CEP
              </Button>
            </div>
          </FeedbackCard>
        ) : null}

        {calculator.status === "invalid_cep" ? (
          <FeedbackCard
            icon={<CircleAlert className="h-5 w-5" />}
            title="CEP inválido."
            text="Confira os números informados."
            tone="danger"
          />
        ) : null}

        {calculator.status === "network_error" ? (
          <FeedbackCard
            icon={<WifiOff className="h-5 w-5" />}
            title="Não foi possível consultar o frete neste momento."
            text="Verifique sua conexão e tente novamente."
          >
            <Button size="sm" onClick={() => void calculator.calculate()}>
              Tentar novamente
            </Button>
          </FeedbackCard>
        ) : null}

        {calculator.status === "server_error" ? (
          <FeedbackCard
            icon={<CircleAlert className="h-5 w-5" />}
            title="Estamos com uma instabilidade temporária no cálculo de frete."
            text="Tente novamente em alguns instantes."
            tone="danger"
          >
            <Button size="sm" onClick={() => void calculator.calculate()}>
              Tentar novamente
            </Button>
          </FeedbackCard>
        ) : null}
      </div>
    </section>
  );
}
